import { PanelBase } from "./PanelBase";
import type { GameContext } from "../../GameContext";

const EQUIPMENT_SLOTS = [
  "weapon", "offhand", "head", "body", "hands", "legs", "feet", "cape", "ring1", "ring2",
] as const;

type EquipmentSlot = (typeof EQUIPMENT_SLOTS)[number];

type EquipmentState = Partial<Record<EquipmentSlot, { itemId: string; name: string; iconKey: string } | null>>;

/**
 * Per 1.8: "Ten equipment slots per character: Weapon, Offhand, Head,
 * Body, Hands, Legs, Feet, Cape, Ring 1, Ring 2". Per M05: all ten slots'
 * equip/unequip live under /api/v1/characters/:id/equipment*.
 */
export class EquipmentPanel extends PanelBase {
  constructor(root: HTMLElement, ctx: GameContext, private readonly characterId: string) {
    super(root, ctx, "equipment-panel");
  }

  protected async refresh(): Promise<void> {
    this.el.innerHTML = "<p>Loading equipment…</p>";
    const result = await this.ctx.network.rest.get<EquipmentState>(
      `/api/v1/characters/${this.characterId}/equipment`
    );

    if (!result.ok) {
      this.el.innerHTML = `<p class="panel-error">${escapeHtml(result.message)}</p>`;
      return;
    }

    this.render(result.data);
  }

  private render(state: EquipmentState): void {
    this.el.innerHTML = "<h3>Equipment</h3>";
    const grid = document.createElement("div");
    grid.className = "equipment-slots";

    for (const slot of EQUIPMENT_SLOTS) {
      const slotEl = document.createElement("div");
      slotEl.className = "equipment-slot";
      slotEl.dataset["slot"] = slot;
      const equipped = state[slot];

      slotEl.innerHTML = `
        <div class="slot-label">${slotLabel(slot)}</div>
        <div class="slot-item">${equipped ? escapeHtml(equipped.name) : "(empty)"}</div>
      `;

      if (equipped) {
        const unequipBtn = document.createElement("button");
        unequipBtn.textContent = "Unequip";
        unequipBtn.addEventListener("click", () => void this.unequip(slot));
        slotEl.appendChild(unequipBtn);
      }

      grid.appendChild(slotEl);
    }
    this.el.appendChild(grid);
  }

  private async unequip(slot: EquipmentSlot): Promise<void> {
    const result = await this.ctx.network.rest.delete(
      `/api/v1/characters/${this.characterId}/equipment/${slot}`
    );
    if (result.ok) await this.refresh();
  }
}

function slotLabel(slot: EquipmentSlot): string {
  const labels: Record<EquipmentSlot, string> = {
    weapon: "Weapon",
    offhand: "Offhand",
    head: "Head",
    body: "Body",
    hands: "Hands",
    legs: "Legs",
    feet: "Feet",
    cape: "Cape",
    ring1: "Ring 1",
    ring2: "Ring 2",
  };
  return labels[slot];
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
